import { Fragment, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { gql, useMutation, useQuery } from "@apollo/client"
import { Banner } from "../../util/Banner"
import tableStyle from "../../../style/table.module.css"
import { useHistory, useParams } from "react-router-dom"
import { TableHeader } from "../../util/table/TableHeader"
import { TableContentLoader } from "../../util/table/TableContentLoader"
import inputStyle from "../../../style/input.module.css"
import { uniqueId } from "lodash"
import buttonStyle from "../../../style/button.module.css"
import formStyle from "../../../style/form.module.css"
import { showError } from "../../../redux/actions/NotificationActions"
import { useDispatch } from "react-redux"
import cn from "classnames"
import { Loader } from "../../util/Loader"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import Select from "react-select"
import makeAnimated from "react-select/animated"
import Markdown from 'react-markdown'

// Sample data configuration for live preview
const PREVIEW_SAMPLE_DATA = {
    name: "400 [0125]",
    sender: "Max Mustermann",
    sender_email: "max.mustermann@phoenixgroup.eu",
    type: "Musterlisteliste",
    ek: "400",
    valid_from: "2025-01-01",
    valid_until: "2025-01-31",
    dat_name: "PHOENIX 400",
    valid_range_de: "Januar (01.01.-14.01.) 2025",
}

// Function to render template with sample data
const renderTemplate = (template, sampleData) => {
    if (!template) return ""
    let rendered = template
    Object.keys(sampleData).forEach((key) => {
        const placeholder = `{${key}}`
        rendered = rendered.replace(new RegExp(placeholder, "g"), sampleData[key])
    })
    return rendered
}

const GET_DISTRIBUTOR = gql`
    query ($id: UUID!) {
        distributor(id: $id) {
            id
            name
            title
            message
            isDefault
            pricingListTypes(
                limit: -1
            ) {
                page {
                    id
                    displayName
                    pricingListTypeNumber
                }
            }
            virtualPricingListTypes {
                id
                displayName
                pricingListTypeNumber
                isVirtual
                isCopyOfNumber
                exportToHagedaFolder
            }
            emails(limit: -1) {
                page {
                    id
                    email
                }
            }
        }
    }
`

const GET_PRICING_LIST_TYPES = gql`
    query {
        pricingListTypes(
            limit: -1
            filterParams: { hideInCreateEquals: false, disabledEquals: false }
        ) {
            page {
                id
                displayName
                pricingListTypeNumber
            }
        }
        virtualPricingListTypes {
            id
            displayName
            pricingListTypeNumber
            isVirtual
            isCopyOfNumber
            exportToHagedaFolder
        }
    }
`

const UPDATE_DISTRIBUTOR = gql`
    mutation (
        $id: UUID!
        $name: String
        $title: String!
        $message: String!
        $emails: [EmailInputType]
        $pricingListTypes: [UUID]
        $virtualPricingListTypeNumbers: [String]
        $isDefault: Boolean
    ) {
        updateDistributor(
            id: $id
            name: $name
            title: $title
            message: $message
            emails: $emails
            pricingListTypes: $pricingListTypes
            virtualPricingListTypeNumbers: $virtualPricingListTypeNumbers
            isDefault: $isDefault
        ) {
            id
            title
            message
            name
            isDefault
            pricingListTypes {
                page {
                    id
                    displayName
                    pricingListTypeNumber
                }
            }
            virtualPricingListTypes {
                id
                displayName
                pricingListTypeNumber
                isVirtual
            }
            emails(limit: -1) {
                page {
                    id
                    email
                }
            }
        }
    }
`

const CREATE_DISTRIBUTOR = gql`
    mutation (
        $name: String!
        $title: String!
        $message: String!
        $emails: [EmailInputType]
        $pricingListTypes: [UUID]
        $virtualPricingListTypeNumbers: [String]
        $isDefault: Boolean
    ) {
        createDistributor(
            name: $name
            title: $title
            message: $message
            emails: $emails
            pricingListTypes: $pricingListTypes
            virtualPricingListTypeNumbers: $virtualPricingListTypeNumbers
            isDefault: $isDefault
        ) {
            id
            name
            title
            message
            isDefault
            pricingListTypes {
                page {
                    id
                    displayName
                    pricingListTypeNumber
                }
            }
            virtualPricingListTypes {
                id
                displayName
                pricingListTypeNumber
                isVirtual
            }
            emails(limit: -1) {
                page {
                    id
                    email
                }
            }
        }
    }
`

export const DistributorEditor = () => {
    const { id } = useParams()
    const [name_input_id] = useState(() => uniqueId("name_input_"))
    const [pricing_list_types_id] = useState(() =>
        uniqueId("pricing_list_types_"),
    )
    const [order, setOrder] = useState([])

    const [name, setName] = useState("")
    const [message, setMessage] = useState("")
    const [title, setTitle] = useState("")
    const [email, setEmail] = useState("")
    const [emails, setEmails] = useState([])
    const [pricingListTypes, setPricingListTypes] = useState([])
    const [virtualPricingListTypeNumbers, setVirtualPricingListTypeNumbers] = useState([])
    const [isDefault, setIsDefault] = useState(false)

    const { t } = useTranslation(["common", "table"])

    const { data, loading } = useQuery(GET_DISTRIBUTOR, {
        variables: {
            id,
        },
        skip: !id,
    })

    const { data: pricingListTypeData, loading: pricingListLoadingData } =
        useQuery(GET_PRICING_LIST_TYPES)

    const history = useHistory()

    const [update, { loading: updateDistributorLoading }] =
        useMutation(UPDATE_DISTRIBUTOR)
    const [create, { loading: createDistributorLoading }] =
        useMutation(CREATE_DISTRIBUTOR)

    const dispatch = useDispatch()

    useEffect(() => {
        setName(data?.distributor?.name || "")
        setTitle(data?.distributor?.title || "")
        setMessage(data?.distributor?.message || "")
        setIsDefault(data?.distributor?.isDefault || false)
        setEmails(
            data?.distributor?.emails?.page?.map((e) => ({
                email: e.email,
                id: e.id,
            })) || [],
        )
        setPricingListTypes(
            data?.distributor?.pricingListTypes?.page?.map((e) => e.id || []),
        )
        setVirtualPricingListTypeNumbers(
            data?.distributor?.virtualPricingListTypes?.map((e) => e.pricingListTypeNumber) || [],
        )
    }, [data?.distributor])

    if (
        loading ||
        createDistributorLoading ||
        updateDistributorLoading ||
        pricingListLoadingData
    ) {
        return (
            <div>
                <Banner text={t("table:distributor")} />
                <Loader />
            </div>
        )
    }

    return (
        <div>
            <Banner
                text={`${t("table:distributor")}: ${
                    name || data?.distributor?.name || ""
                }`}
            />
            <div className={tableStyle.stretchFilterBar}>
                <div className={tableStyle.filterBar} />

                <div>
                    <button
                        className={buttonStyle.button}
                        disabled={!name || !title || !message}
                        onClick={() =>
                            id
                                ? update({
                                      variables: {
                                          id,
                                          name,
                                          title,
                                          message,
                                          emails,
                                          pricingListTypes,
                                          virtualPricingListTypeNumbers,
                                          isDefault,
                                      },
                                  })
                                      .then(() => history.push(`/distributors`))
                                      .catch((e) =>
                                          dispatch(showError(e.message)),
                                      )
                                : create({
                                      variables: {
                                          name,
                                          emails,
                                          title,
                                          message,
                                          pricingListTypes,
                                          virtualPricingListTypeNumbers,
                                          isDefault,
                                      },
                                  })
                                      .then(() => history.push(`/distributors`))
                                      .catch((e) =>
                                          dispatch(showError(e.message)),
                                      )
                        }
                    >
                        {t("table:save")}
                    </button>
                </div>
            </div>
            <div className={formStyle.rowWrapper}>
                <div
                    className={formStyle.legend}
                    style={{ gridTemplateColumns: "auto auto auto" }}
                >
                    <h1 style={{ gridColumn: "1 / -1" }}>
                        {t("table:legend")}
                    </h1>
                    <p></p>
                    <p></p>
                    <p>
                        <strong>{t("table:sample")}</strong>
                    </p>
                    <p>{t("table:pricing_list_name")}</p>
                    <p>{"{name}"}</p>
                    <p>{PREVIEW_SAMPLE_DATA.name}</p>
                    <p>{t("table:pricing_list_type")}</p>
                    <p>{"{type}"}</p>
                    <p>{PREVIEW_SAMPLE_DATA.type}</p>
                    <p>{t("table:pricing_list_type_number")}</p>
                    <p>{"{ek}"}</p>
                    <p>{PREVIEW_SAMPLE_DATA.ek}</p>
                    <p>{t("table:sender_name")}</p>
                    <p>{"{sender}"}</p>
                    <p>{PREVIEW_SAMPLE_DATA.sender}</p>
                    <p>{t("table:sender_email")}</p>
                    <p>{"{sender_email}"}</p>
                    <p>{PREVIEW_SAMPLE_DATA.sender_email}</p>
                    <p>{t("table:valid_from")}</p>
                    <p>{"{valid_from}"}</p>
                    <p>{PREVIEW_SAMPLE_DATA.valid_from}</p>
                    <p>{t("table:valid_until")}</p>
                    <p>{"{valid_until}"}</p>
                    <p>{PREVIEW_SAMPLE_DATA.valid_until}</p>
                    <p>{t("table:dat_filename")}</p>
                    <p>{"{dat_name}"}</p>
                    <p>{PREVIEW_SAMPLE_DATA.dat_name}</p>
                    <p>{t("table:valid_range_de")}</p>
                    <p>{"{valid_range_de}"}</p>
                    <p>{PREVIEW_SAMPLE_DATA.valid_range_de}</p>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", flexGrow: 1 }}>
                    <div>
                        <label htmlFor={name_input_id} style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                            {t("table:name")}
                        </label>
                        <input
                            id={name_input_id}
                            className={inputStyle.text}
                            style={{ width: "100%", boxSizing: "border-box" }}
                            type="text"
                            value={name}
                            onChange={(evt) => setName(evt.target.value)}
                        />
                    </div>

                    <div>
                        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                            <input
                                type="checkbox"
                                checked={isDefault}
                                onChange={(evt) => setIsDefault(evt.target.checked)}
                            />
                            <span>{t("table:default_distributor")}</span>
                        </label>
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                            {t("table:title")}
                        </label>
                        <input
                            className={inputStyle.text}
                            style={{ width: "100%", boxSizing: "border-box" }}
                            type="text"
                            value={title}
                            onChange={(evt) => setTitle(evt.target.value)}
                        />
                        <div
                            style={{
                                marginTop: "0.5rem",
                                padding: "0.5rem",
                                backgroundColor: "#f5f5f5",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "0.9rem",
                            }}
                        >
                            <strong>{t("table:preview")}:</strong>{" "}
                            {renderTemplate(title, PREVIEW_SAMPLE_DATA) ||
                                "(empty)"}
                        </div>
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                            {t("table:message")}
                        </label>
                        <textarea
                            className={inputStyle.textArea}
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                resize: "vertical",
                            }}
                            value={message}
                            rows={15}
                            onChange={(evt) => setMessage(evt.target.value)}
                        />
                        <div
                            style={{
                                marginTop: "0.5rem",
                                padding: "0.5rem",
                                backgroundColor: "#f5f5f5",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "0.9rem",
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            <strong>{t("table:preview")}:</strong>
                            <br />
                            <Markdown >
                            {renderTemplate(message, PREVIEW_SAMPLE_DATA) ||
                                "(empty)"}
                            </Markdown>
                        </div>
                    </div>

                    <div>
                        <label htmlFor={pricing_list_types_id} style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                            {t("table:pricing_list_types")}
                        </label>
                        <Select
                            isMulti={true}
                            value={[
                                // Real pricing list types (UUIDs)
                                ...pricingListTypes?.map((a) => ({
                                    value: a,
                                    label: pricingListTypeData?.pricingListTypes?.page
                                        .map((pt) => ({
                                            ...pt,
                                            fullName: `${pt.displayName} (${pt.pricingListTypeNumber})`,
                                        }))
                                        .find((as) => as.id === a)?.fullName,
                                    isVirtual: false,
                                })) || [],
                                // Virtual pricing list types (strings)
                                ...virtualPricingListTypeNumbers?.map((num) => {
                                    const virtualType = pricingListTypeData?.virtualPricingListTypes?.find(
                                        (vt) => vt.pricingListTypeNumber === num
                                    )
                                    return {
                                        value: num,
                                        label: virtualType ? `${virtualType.displayName} (${virtualType.pricingListTypeNumber})` : num,
                                        isVirtual: true,
                                    }
                                }) || [],
                            ]}
                            onChange={(selectedOptions) => {
                                // Separate real and virtual types
                                const realTypes = selectedOptions.filter((opt) => !opt.isVirtual).map((opt) => opt.value)
                                const virtualTypes = selectedOptions.filter((opt) => opt.isVirtual).map((opt) => opt.value)
                                setPricingListTypes(realTypes)
                                setVirtualPricingListTypeNumbers(virtualTypes)
                            }}
                            loadingMessage={() => t("common:loading")}
                            isLoading={loading}
                            components={makeAnimated()}
                            options={[
                                // Real pricing list types
                                ...(pricingListTypeData?.pricingListTypes?.page?.
                                    filter(a => !pricingListTypeData?.virtualPricingListTypes?.map(
                                        b => b.pricingListTypeNumber
                                )?.includes(a.pricingListTypeNumber))?.
                                    map(
                                    (a) => ({
                                        value: a.id,
                                        label: `${a.displayName} (${a.pricingListTypeNumber})`,
                                        isVirtual: false,
                                    }),
                                ) || []),
                                // Virtual pricing list types (with visual indicator)
                                ...(pricingListTypeData?.virtualPricingListTypes?.map(
                                    (a) => ({
                                        value: a.pricingListTypeNumber,
                                        label: `[C] ${a.displayName} (${a.pricingListTypeNumber})`,
                                        isVirtual: true,
                                    }),
                                ) || []),
                            ]}
                            id={pricing_list_types_id}
                        />
                    </div>
                </div>
            </div>
            <div
                className={tableStyle.table}
                style={{
                    gridTemplateColumns: "1fr 3em",
                }}
            >
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:email")}
                    asc="NameAsc"
                    desc="NameDesc"
                    order={order}
                    setOrder={setOrder}
                />
                <p className={tableStyle.header} />
                {loading && !data?.pricingLists && (
                    <TableContentLoader columns={2} />
                )}
                {emails.map((s, i) => (
                    <Fragment key={i}>
                        <p
                            style={{ textTransform: "unset" }}
                            className={tableStyle.cell}>{s?.email}</p>
                        <p className={tableStyle.actionCell}>
                            <button
                                className={cn(
                                    tableStyle.actionButton,
                                    tableStyle.lastCell,
                                    tableStyle.negativeActionButton,
                                )}
                                onClick={() =>
                                    setEmails(
                                        emails.filter(
                                            (e) =>
                                                !(
                                                    e.email === s?.email &&
                                                    e.id === s?.id
                                                ),
                                        ),
                                    )
                                }
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </p>
                    </Fragment>
                ))}
            </div>
            <div className={formStyle.inputRow}>
                <input
                    className={inputStyle.text}
                    type="email"
                    value={email}
                    onChange={(evt) => setEmail(evt.target.value)}
                />
                <button
                    disabled={!email}
                    className={buttonStyle.button}
                    onClick={() => {
                        setEmails([{ email }, ...emails])
                        setEmail("")
                    }}
                >
                    {t("table:add_email")}
                </button>
            </div>
        </div>
    )
}
